import { Component, Input, OnInit } from '@angular/core'
import { Organisation } from '@app/domain/Organisation'
import { Business } from '@app/domain/Business'
import { Venue } from '@app/domain/Venue'
import { Booking } from '@app/domain/Booking'
import { ProductLibrary } from '@app/domain/ProductLibrary'
import { ProductLibraryService } from '@app/features/management/services/product-library.service'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap'
import { LineItem } from '@app/domain/LineItem'
import { Order } from '@app/domain/Order'
import { v4 as uuidv4 } from 'uuid'
import { Consumable } from '@app/domain/Consumable'

interface DisplayItem {
    id: string
    name: string
    description: string | null
    consumable: Consumable | null
    skuId: string
    price: number
    paidOnline?: boolean
}

@Component({
    selector: 'app-booking-order-edit',
    templateUrl: './booking-order-edit.component.html',
        styles: [`
        :host
            display: flex
            flex-direction: column
            flex-grow: 1
            height: 100%
    `],
})
export class BookingOrderEditComponent implements OnInit {
    @Input() organisation!: Organisation
    @Input() business!: Business
    @Input() venue!: Venue
    @Input() booking!: Booking

    productLibrary: ProductLibrary | null = null
    items: DisplayItem[] = []
    form!: FormGroup
    lineItemsArray!: FormArray<FormGroup>
    currencyCode!: string
    showAddProduct = false
    addSearchTerm = ''

    constructor(
        private productLibraryService: ProductLibraryService,
        private fb: FormBuilder,
        public activeOffcanvas: NgbActiveOffcanvas
    ) { }

    ngOnInit() {
        this.currencyCode = this.booking.order?.currencyCode || this.business.paymentsAccount?.currencyCode || 'GBP'
        this.form = this.fb.group({
            lineItems: this.fb.array([]),
            message: [this.booking.order?.message ?? ''],
        })
        this.lineItemsArray = this.form.get('lineItems') as FormArray<FormGroup>
        if (!this.booking.order?.isPaid) {
            this.loadProductLibrary()
        }
    }

    private loadProductLibrary() {
        this.productLibraryService.getProductLibrary(this.organisation, this.business, this.venue)
            .subscribe(library => {
                this.productLibrary = library
                this.buildDisplayItems()
                this.prepopulateFromExistingOrder()
            })
    }

    private buildDisplayItems() {
        if (!this.productLibrary) {
            this.items = []
            return
        }
        const itemsBySku = new Map<string, DisplayItem>()
        if (this.booking.order) {
            this.booking.order.lineItems.forEach(lineItem => {
                const consumable = this.productLibrary!.getConsumable(lineItem.productId) || null
                const name = consumable?.displayName || lineItem.name
                const description = consumable?.description || null
                const price = consumable ? consumable.getAmountForSKU(lineItem.skuId) : lineItem.amount
                const item: DisplayItem = {
                    id: lineItem.id,
                    name,
                    description,
                    consumable,
                    skuId: lineItem.skuId,
                    price,
                    paidOnline: lineItem.paidOnline,
                }
                itemsBySku.set(item.skuId, item)
            })
        }
        this.items = Array.from(itemsBySku.values())
    }

    private prepopulateFromExistingOrder() {
        if (!this.booking.order) {
            return
        }
        const existing = this.booking.order.lineItems
        existing.forEach(lineItem => {
            this.lineItemsArray.push(this.fb.group({
                id: this.fb.control(lineItem.id),
                name: this.fb.control(lineItem.name, Validators.required),
                productId: this.fb.control(lineItem.productId, Validators.required),
                skuId: this.fb.control(lineItem.skuId, Validators.required),
                amount: this.fb.control(lineItem.amount, Validators.required),
                paidOnline: this.fb.control(lineItem.paidOnline),
                quantity: this.fb.control(lineItem.quantity, [Validators.required, Validators.min(1)])
            }))
        })
    }

    increment(item: DisplayItem) {
        const existing = this.findLineItemBySku(item.skuId)
        if (existing) {
            const qty = existing.get('quantity')!.value + 1
            existing.get('quantity')!.setValue(qty)
            return
        }
        this.lineItemsArray.push(this.fb.group({
            id: this.fb.control(uuidv4()),
            name: this.fb.control(item.name, Validators.required),
            productId: this.fb.control(item.consumable?.id || '' , Validators.required),
            skuId: this.fb.control(item.skuId, Validators.required),
            amount: this.fb.control(item.paidOnline === false ? 0 : item.price, Validators.required),
            paidOnline: this.fb.control(item.paidOnline ?? true),
            quantity: this.fb.control(1, [Validators.required, Validators.min(1)])
        }))
    }

    decrement(item: DisplayItem) {
        const index = this.findLineItemIndexBySku(item.skuId)
        if (index === -1) {
            return
        }
        const control = this.lineItemsArray.at(index)
        const current = control.get('quantity')!.value
        if (current <= 1) {
            this.lineItemsArray.removeAt(index)
        } else {
            control.get('quantity')!.setValue(current - 1)
        }
    }

    getQuantity(item: DisplayItem): number {
        const existing = this.findLineItemBySku(item.skuId)
        return existing ? existing.get('quantity')!.value : 0
    }

    save() {
        const controls = this.lineItemsArray.controls as FormGroup[]
        const now = new Date()
        const existingOrder = this.booking.order
        if (existingOrder?.isPaid) {
            this.activeOffcanvas.close(new Order(
                existingOrder.id, existingOrder.venueId, existingOrder.lineItems, existingOrder.currencyCode,
                this.form.get('message')?.value || null, existingOrder.dateCreated, now, true
            ))
            return
        }
        const lineItems: LineItem[] = controls.map(control => new LineItem(
            control.get('id')!.value,
            control.get('productId')!.value,
            control.get('skuId')!.value,
            control.get('name')!.value,
            control.get('amount')!.value,
            control.get('quantity')!.value,
            existingOrder?.lineItems.find(line => line.id === control.get('id')!.value)?.modifiers ?? [],
            null,
            existingOrder ? existingOrder.dateCreated : now,
            now,
            null,
            control.get('paidOnline')!.value
        ))
        const order = new Order(
            existingOrder ? existingOrder.id : uuidv4(),
            this.venue.id,
            lineItems,
            this.currencyCode,
            existingOrder ? existingOrder.message : null,
            existingOrder ? existingOrder.dateCreated : now,
            now
        )
        this.activeOffcanvas.close(order)
    }

    cancel() {
        this.activeOffcanvas.dismiss('cancel')
    }

    private findLineItemBySku(skuId: string): FormGroup | null {
        return (this.lineItemsArray.controls.find(c => c.get('skuId')!.value === skuId) as FormGroup) || null
    }

    private findLineItemIndexBySku(skuId: string): number {
        return this.lineItemsArray.controls.findIndex(c => c.get('skuId')!.value === skuId)
    }

    toggleAddProduct() {
        this.showAddProduct = !this.showAddProduct
    }

    onAddSearch(term: string) {
        this.addSearchTerm = term?.trim().toLowerCase() || ''
    }

    private get existingSkuIds(): Set<string> {
        const ids = new Set<string>()
        const controls = this.lineItemsArray?.controls || []
        controls.forEach(c => {
            const id = c.get('skuId')?.value
            if (id) ids.add(id)
        })
        return ids
    }

    private defaultSkuId(consumable: Consumable): string | null {
        const sku = consumable.skus?.[0] || null
        return sku ? sku.id : null
    }

    get addableConsumables(): Consumable[] {
        if (!this.productLibrary) {
            return []
        }
        const ids = this.existingSkuIds
        return this.productLibrary.consumables
            .filter(c => Array.isArray(c.skus) && c.skus.length > 0)
            .filter(c => {
                const sid = this.defaultSkuId(c)
                return !!sid && !ids.has(sid)
            })
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
    }

    get filteredConsumables(): Consumable[] {
        const list = this.addableConsumables
        if (!this.addSearchTerm) {
            return list
        }
        const term = this.addSearchTerm
        return list.filter(c => {
            return c.displayName.toLowerCase().includes(term) || (c.description||'').toLowerCase().includes(term)
        })
    }

    getConsumablePrice(consumableId: string): number {
        return (this.productLibrary?.getConsumable(consumableId)?.skus?.[0]?.price ?? 0) / 100
    }

    addConsumable(consumable: Consumable) {
        if (!consumable) {
            return
        }
        const defaultSku = consumable.skus?.[0] || null
        const skuId = defaultSku?.id || ''
        const amount = defaultSku?.price ?? 0
        const existing = this.findLineItemBySku(skuId)
        if (existing) {
            const qty = existing.get('quantity')!.value + 1
            existing.get('quantity')!.setValue(qty)
            return
        }
        if (!this.items.some(i => i.skuId === skuId)) {
            this.items = [
                ...this.items,
                {
                    id: consumable.id,
                    name: consumable.displayName,
                    description: consumable.description || null,
                    consumable: consumable,
                    skuId,
                    price: amount,
                }
            ]
        }
        this.lineItemsArray.push(this.fb.group({
            id: this.fb.control(uuidv4()),
            name: this.fb.control(consumable.displayName, Validators.required),
            productId: this.fb.control(consumable.id, Validators.required),
            skuId: this.fb.control(skuId, Validators.required),
            amount: this.fb.control(amount, Validators.required),
            paidOnline: this.fb.control(true),
            quantity: this.fb.control(1, [Validators.required, Validators.min(1)])
        }))
    }

    removeLineItem(item: DisplayItem) {
        const index = this.findLineItemIndexBySku(item.skuId)
        if (index !== -1) {
            this.lineItemsArray.removeAt(index)
        }
        const stillExists = this.findLineItemBySku(item.skuId) !== null
        if (!stillExists) {
            this.items = this.items.filter(i => i.skuId !== item.skuId)
        }
    }
}
